import { ColoursObj } from "@eventr/common-storybook";
import { Box } from "@mui/material";
import { useRef, type ReactNode } from "react";
import { ScrollDisabledProvider, useScrollDisabled } from "state/scrollState/scrollDisabledContext";
import { ScrollContainerContext } from "state/scrollState/scrollSignals";
import { PaneContext, type PaneSide } from "./pane-context";

interface PaneProps {
  side: PaneSide;
  /** Paints the page background instead of letting what is behind the panes show through. */
  solid?: boolean;
  /** Keeps the pane mounted but out of the layout. */
  hidden?: boolean;
  children: ReactNode;
}

/**
 * One pane of the two-pane layout. The outer box is the containing block for its fixed
 * descendants and the size container `var(--app-width)` and `cqh` measure. The inner box scrolls, with its
 * own scroll container and scroll-disabled state.
 */
export function Pane({ side, solid = false, hidden = false, children }: PaneProps) {
  return (
    <PaneContext.Provider value={side}>
      <ScrollDisabledProvider>
        <Box
          data-pane={side}
          sx={{
            position: "relative",
            display: hidden ? "none" : undefined,
            minWidth: 0,
            height: "100%",
            transform: "translateZ(0)",
            containerType: "size",
            bgcolor: solid ? ColoursObj.backgrounds.primary : undefined,
            "--app-width": "100cqw",
          }}
        >
          <PaneScroller>{children}</PaneScroller>
        </Box>
      </ScrollDisabledProvider>
    </PaneContext.Provider>
  );
}

function PaneScroller({ children }: { children: ReactNode }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { disabled } = useScrollDisabled();

  return (
    <ScrollContainerContext.Provider value={scrollerRef}>
      <Box
        ref={scrollerRef}
        data-pane-scroller
        sx={{
          height: "100%",
          overflowX: "hidden",
          overflowY: disabled ? "hidden" : "auto",
          overscrollBehavior: "contain",
        }}
      >
        {children}
      </Box>
    </ScrollContainerContext.Provider>
  );
}
